import { ValidationError, FetchFailedError, ErrorCode } from './apiError';
import { isLeft } from 'fp-ts/lib/Either';
import { RawApi } from '.';
import { convertTennisTournament, TennisTournament } from './converters/tennis';
import {
  playersInfoApiResponse,
  rankingApiResponse,
  TennisPlayersInfo,
  TennisRankings,
  tennisResultsApiResponse,
} from './validators/tennis';

export interface TennisAPI {
  fetchTennisPlayersDetails: (tourId: string, eventId: string) => Promise<TennisPlayersInfo>;
  fetchTennisRankings: (slug: string, type: 'single' | 'race') => Promise<TennisRankings>;
  fetchTennisMatchResults: (tourId: string, eventId: string) => Promise<TennisTournament>;
}

export class TennisApi extends RawApi implements TennisAPI {
  public async fetchTennisPlayersDetails(
    tourId: string,
    eventId: string,
  ): Promise<TennisPlayersInfo> {
    const endpoint = `/tennis/v5/${tourId}/events/${eventId}/players`;
    return this.fetchSportEvents(endpoint);
  }
  public async fetchTennisMatchResults(
    tourId: string,
    eventId: string,
  ): Promise<TennisTournament> {
    const response = await this.fetchScoresApi(`/tennis/v5/${tourId}/events/${eventId}/results`);

    if (!response.ok) {
      throw new FetchFailedError(response.statusText, response.status as ErrorCode, response);
    }

    const jsonResponse: unknown = await response.json();
    const matchScorecard = tennisResultsApiResponse.decode(jsonResponse);

    if (isLeft(matchScorecard)) {
      throw new ValidationError('API Response validation failed', 400, matchScorecard);
    }

    return convertTennisTournament(matchScorecard.right);
  }

  public async fetchTennisRankings(
    slug: string,
    type: 'single' | 'race',
  ): Promise<TennisRankings> {
    const response = await this.fetchScoresApi(`/tennis/v5/${slug}/ranking/${type}`);
    if (!response.ok) {
      throw new FetchFailedError(response.statusText, response.status as ErrorCode, response);
    }
    const jsonResponse: unknown = await response.json();
    const rankings = rankingApiResponse.decode(jsonResponse);

    if (isLeft(rankings)) {
      throw new ValidationError('API Response validation failed', 400, rankings);
    }

    return rankings.right;
  }
  private async fetchSportEvents(endpoint: string): Promise<TennisPlayersInfo> {
    const response = await this.fetchScoresApi(endpoint);

    if (!response.ok) {
      throw new FetchFailedError(response.statusText, response.status as ErrorCode, response);
    }

    const jsonResponse: unknown = await response.json();
    const tennisPlayersInfo = playersInfoApiResponse.decode(jsonResponse);

    if (isLeft(tennisPlayersInfo)) {
      throw new ValidationError('API Response validation failed', 400, tennisPlayersInfo);
    }

    return tennisPlayersInfo.right;
  }
}
